/**
 * Spec 218 PR4 — Emitted-invoice success screen (`/invoices/new/emitted`).
 *
 * Web port of `mobile/src/screens/EmittedInvoiceScreen.tsx` (sin PDF
 * todavía: el botón Descargar/Compartir llega en PR5). Reads draft +
 * EmitResponse from location.state. If absent (cold reload) bounces to
 * /invoices to avoid showing a blank success surface.
 *
 * Includes:
 *   - Big "OK" icon + AEAT status pill (accepted / pending / rejected).
 *   - QR base64 + verification URL.
 *   - Series / number / total summary.
 *   - Email mailto if recipient.email is set.
 *   - "Volver al listado" + "Ver detalle" CTAs.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { CheckCircle2, Clock, Download, FileText, List, Mail } from 'lucide-react';

import { getFiscalProfile, type EmitResponse, type InvoicingCtx } from '../sync/invoicing';
import { downloadInvoicePdf } from '../utils/invoicePdf';
import type { FiscalProfile, InvoiceDraft } from '@cuadrilla/types/invoice';
import type { WebIdentity } from '../store/keys';

import './EmittedInvoice.css';

interface EmittedInvoiceProps {
  identity: WebIdentity;
}

interface NavState {
  draft?: InvoiceDraft;
  response?: EmitResponse;
}

const STATUS_LABEL: Record<string, string> = {
  accepted: 'Aceptada por AEAT',
  pending: 'Pendiente de respuesta AEAT',
  rejected: 'Rechazada por AEAT',
};

const STATUS_TONE: Record<string, string> = {
  accepted: 'em-status-ok',
  pending: 'em-status-pending',
  rejected: 'em-status-err',
};

export function EmittedInvoice({ identity }: EmittedInvoiceProps) {
  const location = useLocation();
  const navState = (location.state ?? null) as NavState | null;
  const draft = navState?.draft;
  const response = navState?.response;

  const [profile, setProfile] = useState<FiscalProfile | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Hydrate the fiscal profile so the PDF can be assembled with the
  // issuer block. The user can click Descargar before this resolves —
  // we render a disabled button until the profile lands so we never
  // produce a PDF with an empty issuer.
  useEffect(() => {
    if (!draft || !response) return;
    let cancelled = false;
    const ctx: InvoicingCtx = { companyToken: identity.companyToken };
    getFiscalProfile(ctx)
      .then((p) => { if (!cancelled) setProfile(p); })
      .catch(() => { if (!cancelled) setProfile(null); });
    return () => { cancelled = true; };
  }, [draft, response, identity.companyToken]);

  if (!draft || !response) {
    return <Navigate to="/invoices" replace />;
  }

  async function onDownloadPdf() {
    if (!profile || !draft || !response) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadInvoicePdf({ profile, draft, response });
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  }

  const statusKey = response.aeatStatus ?? 'pending';
  const statusLabel = STATUS_LABEL[statusKey] ?? statusKey;
  const statusTone = STATUS_TONE[statusKey] ?? '';

  const mailtoHref = draft.recipient.email
    ? buildMailtoHref(draft, response)
    : null;

  return (
    <main className="em-shell" data-testid="emitted-invoice-screen">
      <div className="em-card">
        <div className="em-icon">
          <CheckCircle2 size={64} strokeWidth={2} aria-hidden="true" />
        </div>
        <h1 className="em-title" data-testid="emitted-invoice-title">
          Factura emitida
        </h1>
        <p className="em-subtitle">
          Hemos enviado la factura al sistema Verifactu de la AEAT.
        </p>

        <div className={`em-status ${statusTone}`} data-testid="emitted-invoice-status">
          <Clock size={18} strokeWidth={2} aria-hidden="true" />
          <span>{statusLabel}</span>
        </div>

        {response.qrBase64 && (
          <figure className="em-qr" data-testid="emitted-invoice-qr">
            <img
              src={`data:image/png;base64,${response.qrBase64}`}
              alt="Código QR Verifactu"
              className="em-qr-img"
            />
            <figcaption className="em-qr-caption">VERI*FACTU</figcaption>
            {response.qrUrl && (
              <a
                href={response.qrUrl}
                target="_blank"
                rel="noreferrer"
                className="em-qr-url"
                data-testid="emitted-invoice-qr-url"
              >
                {response.qrUrl}
              </a>
            )}
          </figure>
        )}

        <dl className="em-summary" data-testid="emitted-invoice-summary">
          <div className="em-summary-row">
            <dt>Serie / Nº</dt>
            <dd>{draft.series} · {draft.number}</dd>
          </div>
          <div className="em-summary-row">
            <dt>Cliente</dt>
            <dd>{draft.recipient.name || '—'}</dd>
          </div>
          <div className="em-summary-row em-summary-row-bold">
            <dt>Total</dt>
            <dd data-testid="emitted-invoice-total">{response.total.toFixed(2)} €</dd>
          </div>
        </dl>

        {mailtoHref && (
          <a
            href={mailtoHref}
            className="em-primary"
            data-testid="emitted-invoice-email"
          >
            <Mail size={18} strokeWidth={2} aria-hidden="true" />
            <span>Enviar por email</span>
          </a>
        )}

        <button
          type="button"
          className="em-secondary"
          onClick={onDownloadPdf}
          disabled={!profile || downloading}
          data-testid="emitted-invoice-download"
        >
          <Download size={18} strokeWidth={2} aria-hidden="true" />
          <span>{downloading ? 'Generando…' : 'Descargar PDF'}</span>
        </button>
        {downloadError && (
          <p className="em-error" role="alert" data-testid="emitted-invoice-download-error">
            No se pudo generar el PDF: {downloadError}
          </p>
        )}

        <div className="em-actions">
          <Link
            to={`/invoices/${response.id}`}
            className="em-secondary"
            data-testid="emitted-invoice-detail"
          >
            <FileText size={18} strokeWidth={2} aria-hidden="true" />
            <span>Ver detalle</span>
          </Link>
          <Link
            to="/invoices"
            className="em-link"
            data-testid="emitted-invoice-back-to-list"
          >
            <List size={16} strokeWidth={2} aria-hidden="true" />
            <span>Volver al listado</span>
          </Link>
        </div>
      </div>
    </main>
  );
}

function buildMailtoHref(draft: InvoiceDraft, response: EmitResponse): string {
  const subject = `Factura ${draft.series} ${draft.number}`;
  const verifyLine = response.qrUrl
    ? `Puedes verificarla en ${response.qrUrl}.`
    : 'Puedes verificarla en la sede electrónica de la AEAT.';
  const body = `Adjuntamos la factura ${draft.series} ${draft.number} por un total de ${response.total.toFixed(2)} €. ${verifyLine}`;
  const email = draft.recipient.email ?? '';
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
